import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  TablePagination,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Grid,
  Button,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon, 
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  FileDownload as DownloadIcon,
  School as SchoolIcon,
  Gavel as ComplianceIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import api from '../services/api';
import * as XLSX from 'xlsx';

const translateRole = (role: string) => {
  const roles: any = {
    'ADMIN': 'ผู้ดูแลระบบ (Admin)',
    'STAFF': 'เจ้าหน้าที่ (Staff)',
    'SUPERVISOR': 'อาจารย์นิเทศก์',
    'CO_TEACHER': 'ครูพี่เลี้ยง',
    'STUDENT': 'นักศึกษา',
    'DEAN': 'ผู้บริหาร (Dean)'
  };
  return roles[role] || role;
};

const ACTION_TRANSLATIONS: Record<string, { label: string; desc: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  'LOGIN': { label: 'เข้าสู่ระบบ', desc: 'ผู้ใช้งานลงชื่อเข้าสู่ระบบ', color: 'info' },
  'GOOGLE_LOGIN': { label: 'เข้าสู่ระบบ (Google)', desc: 'ลงชื่อเข้าใช้ผ่าน KPRU Google Workspace', color: 'info' },
  'CHANGE_PASSWORD': { label: 'เปลี่ยนรหัสผ่าน', desc: 'ผู้ใช้งานเปลี่ยนรหัสผ่านบัญชีของตนเอง', color: 'warning' },
  'ADMIN_RESET_PASSWORD': { label: 'รีเซ็ตรหัสผ่าน', desc: 'ผู้ดูแลระบบรีเซ็ตรหัสผ่านให้ผู้ใช้งาน', color: 'warning' },
  
  'CREATE_STUDENT': { label: 'เพิ่มนักศึกษา', desc: 'สร้างข้อมูลนักศึกษาใหม่ในระบบ', color: 'success' },
  'UPDATE_STUDENT': { label: 'แก้ไขนักศึกษา', desc: 'ปรับปรุงข้อมูลรายละเอียดนักศึกษา', color: 'warning' },
  'SOFT_DELETE_STUDENT': { label: 'ย้ายนักศึกษาลงถังขยะ', desc: 'ลบข้อมูลนักศึกษาชั่วคราว', color: 'error' },
  'RESTORE_STUDENT': { label: 'กู้คืนนักศึกษา', desc: 'กู้คืนข้อมูลนักศึกษาจากถังขยะ', color: 'success' },
  'PERMANENT_DELETE_STUDENT': { label: 'ลบนักศึกษาถาวร', desc: 'ลบข้อมูลนักศึกษาและบัญชีผู้ใช้อย่างถาวร', color: 'error' },
  'BULK_CREATE_STUDENTS': { label: 'นำเข้านักศึกษา (Bulk)', desc: 'นำเข้าข้อมูลนักศึกษาชุดใหญ่ผ่านไฟล์', color: 'success' },
  'BULK_SOFT_DELETE_STUDENTS': { label: 'ย้ายนักศึกษาหลายคนลงถังขยะ', desc: 'ลบข้อมูลนักศึกษาชั่วคราวแบบกลุ่ม', color: 'error' },
  'BULK_RESTORE_STUDENTS': { label: 'กู้คืนนักศึกษาหลายคน', desc: 'กู้คืนข้อมูลนักศึกษาจากถังขยะแบบกลุ่ม', color: 'success' },
  'BULK_PERMANENT_DELETE_STUDENTS': { label: 'ลบนักศึกษาถาวรหลายคน', desc: 'ลบข้อมูลนักศึกษาถาวรแบบกลุ่ม', color: 'error' },

  'CREATE_SUPERVISOR': { label: 'เพิ่มอาจารย์นิเทศก์', desc: 'สร้างข้อมูลอาจารย์นิเทศก์ใหม่', color: 'success' },
  'UPDATE_SUPERVISOR': { label: 'แก้ไขอาจารย์นิเทศก์', desc: 'ปรับปรุงข้อมูลอาจารย์นิเทศก์', color: 'warning' },
  'SOFT_DELETE_SUPERVISOR': { label: 'ย้ายอาจารย์นิเทศก์ลงถังขยะ', desc: 'ลบอาจารย์นิเทศก์ชั่วคราว', color: 'error' },
  'RESTORE_SUPERVISOR': { label: 'กู้คืนอาจารย์นิเทศก์', desc: 'กู้คืนอาจารย์นิเทศก์จากถังขยะ', color: 'success' },
  'PERMANENT_DELETE_SUPERVISOR': { label: 'ลบอาจารย์นิเทศก์ถาวร', desc: 'ลบข้อมูลอาจารย์นิเทศก์และบัญชีถาวร', color: 'error' },
  'BULK_IMPORT_SUPERVISORS': { label: 'นำเข้าอาจารย์นิเทศก์ (Bulk)', desc: 'นำเข้าอาจารย์นิเทศก์หลายรายการ', color: 'success' },
  'BULK_SOFT_DELETE_SUPERVISORS': { label: 'ย้ายอาจารย์นิเทศก์หลายคนลงถังขยะ', desc: 'ลบอาจารย์นิเทศก์ชั่วคราวแบบกลุ่ม', color: 'error' },
  'BULK_RESTORE_SUPERVISORS': { label: 'กู้คืนอาจารย์นิเทศก์หลายคน', desc: 'กู้คืนอาจารย์นิเทศก์จากถังขยะแบบกลุ่ม', color: 'success' },
  'BULK_PERMANENT_DELETE_SUPERVISORS': { label: 'ลบอาจารย์นิเทศก์ถาวรหลายคน', desc: 'ลบอาจารย์นิเทศก์ถาวรแบบกลุ่ม', color: 'error' },

  'CREATE_CO_TEACHER': { label: 'เพิ่มครูพี่เลี้ยง', desc: 'สร้างข้อมูลครูพี่เลี้ยงใหม่', color: 'success' },
  'UPDATE_CO_TEACHER': { label: 'แก้ไขครูพี่เลี้ยง', desc: 'ปรับปรุงข้อมูลครูพี่เลี้ยง', color: 'warning' },
  'SOFT_DELETE_CO_TEACHER': { label: 'ย้ายครูพี่เลี้ยงลงถังขยะ', desc: 'ลบครูพี่เลี้ยงชั่วคราว', color: 'error' },
  'RESTORE_CO_TEACHER': { label: 'กู้คืนครูพี่เลี้ยง', desc: 'กู้คืนครูพี่เลี้ยงจากถังขยะ', color: 'success' },
  'PERMANENT_DELETE_CO_TEACHER': { label: 'ลบครูพี่เลี้ยงถาวร', desc: 'ลบครูพี่เลี้ยงและบัญชีถาวร', color: 'error' },
  'BULK_IMPORT_CO_TEACHERS': { label: 'นำเข้าครูพี่เลี้ยง (Bulk)', desc: 'นำเข้าข้อมูลครูพี่เลี้ยงหลายรายการ', color: 'success' },
  'BULK_SOFT_DELETE_CO_TEACHERS': { label: 'ย้ายครูพี่เลี้ยงหลายคนลงถังขยะ', desc: 'ลบครูพี่เลี้ยงชั่วคราวแบบกลุ่ม', color: 'error' },
  'BULK_RESTORE_CO_TEACHERS': { label: 'กู้คืนครูพี่เลี้ยงหลายคน', desc: 'กู้คืนครูพี่เลี้ยงจากถังขยะแบบกลุ่ม', color: 'success' },
  'BULK_PERMANENT_DELETE_CO_TEACHERS': { label: 'ลบครูพี่เลี้ยงถาวรหลายคน', desc: 'ลบครูพี่เลี้ยงถาวรแบบกลุ่ม', color: 'error' },

  'AUTO_MATCH_PLACEMENTS': { label: 'จับคู่จัดสรรอัตโนมัติ', desc: 'ประมวลผลจับคู่นักศึกษากับโรงเรียนและอาจารย์นิเทศก์', color: 'primary' },
  'ASSIGN_SUPERVISORS': { label: 'มอบหมายอาจารย์นิเทศก์', desc: 'กำหนดอาจารย์นิเทศก์ประจำตัวนักศึกษา', color: 'info' },
  'UPDATE_PLACEMENT': { label: 'แก้ไขการจัดสรร', desc: 'ปรับปรุงข้อมูลการจัดสรรนักศึกษา', color: 'warning' },
  'SOFT_DELETE_PLACEMENT': { label: 'ลบการจัดสรรชั่วคราว', desc: 'ย้ายรายการจัดสรรลงถังขยะ', color: 'error' },
  'RESTORE_PLACEMENT': { label: 'กู้คืนการจัดสรร', desc: 'กู้คืนรายการจัดสรรจากถังขยะ', color: 'success' },
  'PERMANENT_DELETE_PLACEMENT': { label: 'ลบการจัดสรรถาวร', desc: 'ลบรายการจัดสรรอย่างถาวร', color: 'error' },
  'BULK_UPDATE_COURSES': { label: 'กำหนดรายวิชาแบบกลุ่ม', desc: 'อัปเดตรายวิชาให้นักศึกษาหลายคน', color: 'primary' },

  'CREATE_SCHOOL': { label: 'เพิ่มโรงเรียน', desc: 'บันทึกโรงเรียนฝึกประสบการณ์ใหม่', color: 'success' },
  'UPDATE_SCHOOL': { label: 'แก้ไขโรงเรียน', desc: 'ปรับปรุงข้อมูลโรงเรียน', color: 'warning' },
  'SOFT_DELETE_SCHOOL': { label: 'ย้ายโรงเรียนลงถังขยะ', desc: 'ลบโรงเรียนชั่วคราว', color: 'error' },
  'RESTORE_SCHOOL': { label: 'กู้คืนโรงเรียน', desc: 'กู้คืนโรงเรียนจากถังขยะ', color: 'success' },
  'PERMANENT_DELETE_SCHOOL': { label: 'ลบโรงเรียนถาวร', desc: 'ลบข้อมูลโรงเรียนถาวร', color: 'error' },

  'CREATE_COURSE': { label: 'เพิ่มรายวิชา', desc: 'สร้างรายวิชาฝึกปฏิบัติการสอนใหม่', color: 'success' },
  'UPDATE_COURSE': { label: 'แก้ไขรายวิชา', desc: 'ปรับปรุงข้อมูลรายวิชา', color: 'warning' },
  'DELETE_COURSE': { label: 'ลบรายวิชา', desc: 'ลบรายวิชาออกจากระบบ', color: 'error' },

  'SUBMIT_EVALUATION': { label: 'ส่งผลการประเมิน', desc: 'บันทึกคะแนนและประเมินผลการสอนของนักศึกษา', color: 'success' },
  'RESET_EVALUATION': { label: 'รีเซ็ตผลการประเมิน', desc: 'ล้างคะแนนประเมินเพื่อให้อนุญาตประเมินใหม่', color: 'error' },

  'SYSTEM_BACKUP_AUTO': { label: 'สำรองฐานข้อมูลอัตโนมัติ (00:00 น.)', desc: 'ระบบทำงานเบื้องหลังสำรองฐานข้อมูลประจำวัน', color: 'info' },
  'SYSTEM_BACKUP_AUTO_FAILED': { label: 'สำรองข้อมูลอัตโนมัติล้มเหลว', desc: 'เกิดข้อผิดพลาดในการสำรองฐานข้อมูลอัตโนมัติ', color: 'error' },
  'MANUAL_BACKUP_CREATE': { label: 'สร้างจุดสำรองข้อมูล', desc: 'ผู้ดูแลระบบสั่งสำรองฐานข้อมูลด้วยตนเอง', color: 'info' },
  'RESTORE_DATABASE': { label: 'กู้คืนฐานข้อมูล', desc: 'คืนค่าระบบจากไฟล์จุดสำรองข้อมูล', color: 'warning' },
  'RESTORE_DATABASE_UPLOAD': { label: 'กู้คืนฐานข้อมูล (ไฟล์อัปโหลด)', desc: 'คืนค่าระบบจากไฟล์ SQL ที่อัปโหลดเข้ามา', color: 'warning' },

  'CREATE_ANNOUNCEMENT': { label: 'สร้างประกาศข่าวสาร', desc: 'เผยแพร่ประกาศข่าวสารใหม่', color: 'success' },
  'DELETE_ANNOUNCEMENT': { label: 'ลบประกาศข่าวสาร', desc: 'ลบประกาศข่าวสาร', color: 'error' },
  'CREATE_SYSTEM_USER': { label: 'เพิ่มผู้ใช้ระบบ', desc: 'สร้างบัญชีผู้ใช้งานใหม่ในระบบ', color: 'success' },
  'UPDATE_SYSTEM_USER': { label: 'แก้ไขผู้ใช้ระบบ', desc: 'แก้ไขข้อมูลหรือสถานะผู้ใช้งาน', color: 'warning' },
  'UPDATE_PROFILE': { label: 'แก้ไขข้อมูลส่วนตัว', desc: 'ปรับปรุงข้อมูลโปรไฟล์ของตนเอง', color: 'info' },
};

const FIELD_LABELS: Record<string, string> = {
  count: 'จำนวนรายการ',
  title: 'หัวข้อ/ชื่อเรื่อง',
  student_code: 'รหัสนักศึกษา',
  student_name: 'ชื่อ-นามสกุลนักศึกษา (ผู้รับการประเมิน)',
  major: 'สาขาวิชา',
  course_code: 'รหัสรายวิชา',
  course_name: 'ชื่อรายวิชา',
  course_id: 'รหัสอ้างอิงวิชา',
  first_name: 'ชื่อจริง',
  last_name: 'นามสกุล',
  name: 'ชื่อ-นามสกุล/รายการ',
  prefix: 'คำนำหน้า',
  username: 'ชื่อผู้ใช้',
  email: 'อีเมล',
  semester: 'ภาคเรียน',
  academic_year: 'ปีการศึกษา',
  file_name: 'ชื่อไฟล์สำรอง',
  source_file: 'ไฟล์ต้นฉบับที่กู้คืน',
  uploaded_file: 'ไฟล์ SQL ที่อัปโหลด',
  file_size: 'ขนาดไฟล์',
  retention_cleaned: 'จำนวนไฟล์เก่าที่ล้างอัตโนมัติ (เกิน 90 วัน)',
  status: 'สถานะการทำงาน',
  evaluator_role: 'รอบการประเมิน',
  total_score: 'คะแนนประเมินรวม (%)',
  placement_id: 'รหัสการจัดสรร (Placement ID)',
  school_name: 'สถานศึกษา/โรงเรียน',
  school_id: 'รหัสโรงเรียน',
  target_user_id: 'รหัสผู้ใช้เป้าหมาย',
  department: 'สาขาวิชา/ภาควิชา',
  position: 'กลุ่มสาระ/ตำแหน่ง',
  phone_number: 'เบอร์โทรศัพท์',
  error: 'ข้อความแจ้งเตือนข้อผิดพลาด',
  role: 'สิทธิ์ผู้ใช้งาน',
  type: 'ประเภท'
};

const getActionInfo = (action: string) => {
  if (ACTION_TRANSLATIONS[action]) {
    return ACTION_TRANSLATIONS[action];
  }
  const act = action.toUpperCase();
  let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
  if (act.includes('DELETE') || act.includes('REMOVE') || act.includes('WIPE') || act.includes('DESTROY')) {
    color = 'error';
  } else if (act.includes('UPDATE') || act.includes('EDIT') || act.includes('MODIFY') || act.includes('CHANGE') || act.includes('RESET')) {
    color = 'warning';
  } else if (act.includes('CREATE') || act.includes('ADD') || act.includes('INSERT') || act.includes('REGISTER')) {
    color = 'success';
  } else if (act.includes('LOGIN') || act.includes('LOGOUT') || act.includes('BACKUP')) {
    color = 'info';
  }
  return { label: action, desc: action, color };
};

const formatDetails = (details: string, log: any) => {
  if (!details) return <Typography variant="caption" color="textSecondary">ไม่มีรายละเอียดเพิ่มเติม</Typography>;
  try {
    const parsed = JSON.parse(details);
    if (typeof parsed !== 'object' || parsed === null) {
      return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{details}</Typography>;
    }

    const actionInfo = getActionInfo(log.action);
    const actorName = log.full_name || log.username || 'ระบบอัตโนมัติ';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Deep Action Summary Card */}
        <Card variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 2, borderLeft: '4px solid', borderColor: `${actionInfo.color}.main` }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoIcon fontSize="small" color={actionInfo.color as any} />
              สรุปกิจกรรม: {actionInfo.label} ({log.action})
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {actionInfo.desc}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" icon={<PersonIcon />} label={`ผู้กระทำ: ${actorName}`} variant="outlined" />
              {log.user_role && <Chip size="small" label={`บทบาท: ${translateRole(log.user_role)}`} color="primary" variant="outlined" />}
              {log.ip_address && <Chip size="small" icon={<PublicIcon />} label={`IP: ${log.ip_address}`} color="info" variant="outlined" />}
              {parsed.count !== undefined && (
                <Chip size="small" label={`จำนวน: ${parsed.count} รายการ`} color="secondary" sx={{ fontWeight: 'bold' }} />
              )}
              {parsed.total_score !== undefined && (
                <Chip size="small" label={`คะแนน: ${parsed.total_score}%`} color="success" sx={{ fontWeight: 'bold' }} />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Evaluated Student Summary Card (For SUBMIT_EVALUATION / RESET_EVALUATION) */}
        {(parsed.student_code || parsed.student_name) && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9ff', borderColor: '#bae6fd', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <SchoolIcon fontSize="small" /> ข้อมูลนักศึกษาที่ได้รับการประเมิน (Evaluated Student)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>รหัสนักศึกษา</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: 'primary.dark' }}>
                  {parsed.student_code || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>ชื่อ - นามสกุลนักศึกษา</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {parsed.student_name || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>สาขาวิชา</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  {parsed.major ? <Chip label={parsed.major} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} /> : '-'}
                </Typography>
              </Grid>
              {parsed.school_name && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>สถานศึกษาฝึกประสบการณ์วิชาชีพ</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{parsed.school_name}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Changes: Before / After Diff */}
        {(parsed.hasOwnProperty('old') || parsed.hasOwnProperty('new')) && (
          <Grid container spacing={2}>
            {parsed.old && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff5f5', borderColor: '#ffe3e3', borderRadius: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block', mb: 1 }}>
                    ข้อมูลเดิม (Before)
                  </Typography>
                  <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(parsed.old, null, 2)}
                  </pre>
                </Paper>
              </Grid>
            )}
            {parsed.new && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f6ffed', borderColor: '#d9f7be', borderRadius: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', display: 'block', mb: 1 }}>
                    ข้อมูลใหม่ (After)
                  </Typography>
                  <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(parsed.new, null, 2)}
                  </pre>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Key-Value Breakdown */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#fafafa' }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
            พารามิเตอร์และรายละเอียดเชิงลึก (Parameters & Details)
          </Typography>
          <Grid container spacing={1.5}>
            {Object.entries(parsed)
              .filter(([key]) => key !== 'old' && key !== 'new')
              .map(([key, val]) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                  <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #eee' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block' }}>
                      {FIELD_LABELS[key] || key}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', wordBreak: 'break-all', mt: 0.2 }}>
                      {typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? '-')}
                    </Typography>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Paper>
      </Box>
    );
  } catch (e) {
    return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{details}</Typography>;
  }
};

const CollapsibleRow = ({ log }: { log: any }) => {
  const [open, setOpen] = useState(false);
  const actionInfo = getActionInfo(log.action);

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 50 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(log.created_at).toLocaleDateString('th-TH')}</Typography>
          <Typography variant="caption" color="textSecondary">{new Date(log.created_at).toLocaleTimeString('th-TH')}</Typography>
        </TableCell>
        <TableCell>
          {log.full_name ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {log.full_name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                @{log.username}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {log.username || 'System (ระบบอัตโนมัติ)'}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {log.user_role ? (
            <Chip 
              label={translateRole(log.user_role)} 
              size="small" 
              variant="outlined" 
              color="primary" 
              sx={{ fontSize: '0.75rem', fontWeight: 500 }} 
            />
          ) : (
            <Chip label="ระบบอัตโนมัติ" size="small" variant="outlined" sx={{ color: 'text.secondary', fontSize: '0.75rem' }} />
          )}
        </TableCell>
        <TableCell>
          <Box>
            <Chip 
              label={actionInfo.label} 
              size="small" 
              color={actionInfo.color as any} 
              sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
            />
            {(() => {
              let detailsObj: any = null;
              if (log.details) {
                try { detailsObj = typeof log.details === 'string' ? JSON.parse(log.details) : log.details; } catch (e) {}
              }
              if (detailsObj?.total_score !== undefined) {
                return (
                  <Chip 
                    label={`คะแนน: ${detailsObj.total_score}%`} 
                    size="small" 
                    color="success" 
                    variant="outlined" 
                    sx={{ ml: 0.5, height: 20, fontSize: '0.65rem', fontWeight: 'bold' }} 
                  />
                );
              }
              return null;
            })()}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.3 }}>
              {log.action}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {(() => {
            let detailsObj: any = null;
            if (log.details) {
              try { detailsObj = typeof log.details === 'string' ? JSON.parse(log.details) : log.details; } catch (e) {}
            }
            const hasStudentInfo = detailsObj && (detailsObj.student_name || detailsObj.student_code);

            if ((log.action === 'SUBMIT_EVALUATION' || log.action === 'RESET_EVALUATION') && hasStudentInfo) {
              return (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {detailsObj.student_name || detailsObj.student_code}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                    {detailsObj.student_code ? `${detailsObj.student_code} ` : ''}{detailsObj.major ? `(${detailsObj.major})` : ''}
                  </Typography>
                </Box>
              );
            }

            return <Chip label={log.entity_type || '-'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
          })()}
        </TableCell>
        <TableCell>
          {log.ip_address ? (
            <Chip 
              icon={<PublicIcon fontSize="inherit" />}
              label={log.ip_address} 
              size="small" 
              color={log.ip_address.startsWith('127.') ? 'default' : 'info'}
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} 
            />
          ) : (
            <Typography variant="caption" color="textSecondary">-</Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2.5, bgcolor: '#fbfcfd', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" /> รายละเอียดกิจกรรมเชิงลึก (Detailed Audit Breakdown)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Log ID: {log.id} | บันทึกเมื่อ: {new Date(log.created_at).toLocaleString('th-TH')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {formatDetails(log.details, log)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLogs = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Timeframe & Retention State
  const [timeframe, setTimeframe] = useState('90');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Search & Filters State
  const [searchUser, setSearchUser] = useState('');
  const [searchRole, setSearchRole] = useState('ALL');
  const [searchIp, setSearchIp] = useState('');
  const [searchAction, setSearchAction] = useState('');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchLogs = async (tf = timeframe, start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const params: any = { timeframe: tf };
      if (tf === 'custom') {
        if (start) params.startDate = start;
        if (end) params.endDate = end;
      }
      const response = await api.get('/audit', { params });
      setLogs(response.data || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeframeChange = (newTf: string) => {
    setTimeframe(newTf);
    setPage(0);
    if (newTf !== 'custom') {
      fetchLogs(newTf, startDate, endDate);
    }
  };

  const handleCustomDateSearch = () => {
    setPage(0);
    fetchLogs('custom', startDate, endDate);
  };

  const handleClearFilters = () => {
    setSearchUser('');
    setSearchRole('ALL');
    setSearchIp('');
    setSearchAction('');
    setPage(0);
  };

  // Export Excel Function
  const handleExportExcel = () => {
    try {
      setExporting(true);
      const dataToExport = filteredLogs.map((log: any, idx: number) => {
        let detailsObj: any = {};
        if (log.details) {
          try {
            detailsObj = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
          } catch (e) {}
        }
        
        const actionInfo = ACTION_TRANSLATIONS[log.action] || { label: log.action, desc: '' };
        
        return {
          'ลำดับ': idx + 1,
          'วัน-เวลา': new Date(log.created_at).toLocaleString('th-TH'),
          'ชื่อผู้ใช้งาน (Username)': log.username || 'SYSTEM',
          'ชื่อ-นามสกุล': log.full_name || '-',
          'ประเภทผู้ใช้งาน': translateRole(log.user_role),
          'กิจกรรม (Action)': log.action,
          'คำอธิบายกิจกรรม': actionInfo.label,
          'รหัสนักศึกษา': detailsObj.student_code || '',
          'ชื่อนักศึกษาที่รับการประเมิน': detailsObj.student_name || '',
          'สาขาวิชา': detailsObj.major || '',
          'โรงเรียน': detailsObj.school_name || '',
          'รอบการประเมิน / ข้อมูลคะแนน': detailsObj.score !== undefined ? `${detailsObj.score} คะแนน (${detailsObj.round || detailsObj.role || ''})` : (detailsObj.round || ''),
          'เป้าหมายข้อมูล (Entity)': log.entity_type ? `${log.entity_type} #${log.entity_id || ''}` : '-',
          'IP Address': log.ip_address || '-',
          'User Agent': log.user_agent || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Logs');
      
      const fileName = `audit_logs_${timeframe === '90' ? '90days' : timeframe}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Export Excel failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Filter logic: includes searching by student code, name, and major inside details
  const filteredLogs = logs.filter((log: any) => {
    const userMatchTarget = `${log.username || ''} ${log.full_name || ''}`.toLowerCase();
    const matchUser = !searchUser || userMatchTarget.includes(searchUser.toLowerCase()) || (!log.username && searchUser.toLowerCase() === 'system');
    const matchRole = searchRole === 'ALL' || log.user_role === searchRole;
    const matchIp = !searchIp || (log.ip_address && log.ip_address.includes(searchIp));

    let detailsStr = '';
    if (log.details) {
      detailsStr = typeof log.details === 'string' ? log.details : JSON.stringify(log.details);
    }
    const matchAction = !searchAction || 
      (log.action && log.action.toLowerCase().includes(searchAction.toLowerCase())) || 
      (ACTION_TRANSLATIONS[log.action]?.label.toLowerCase().includes(searchAction.toLowerCase())) ||
      detailsStr.toLowerCase().includes(searchAction.toLowerCase());

    return matchUser && matchRole && matchIp && matchAction;
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 'bold' }}>
        บันทึกกิจกรรมในระบบ (Activity Audit Logs)
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2.5 }}>
        ระบบบันทึกประวัติการกระทำสำคัญ เช่น การเข้าสู่ระบบ การประเมินผล การสร้าง แก้ไข ลบข้อมูล เพื่อความปลอดภัยและการตรวจสอบย้อนหลัง
      </Typography>

      {/* 90-Day Retention Compliance Banner */}
      <Paper 
        sx={{ 
          p: 2.5, 
          mb: 3, 
          borderRadius: 2, 
          bgcolor: '#f0fdf4', 
          border: '1px solid #bbf7d0',
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          justifyContent: 'space-between',
          gap: 2 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box 
            sx={{ 
              p: 1.2, 
              borderRadius: '50%', 
              bgcolor: '#dcfce7', 
              color: '#16a34a',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <ComplianceIcon fontSize="medium" />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#166534' }}>
                ระบบจัดเก็บบันทึกประวัติกิจกรรมตาม พ.ร.บ. คอมพิวเตอร์ (ไม่น้อยกว่า 90 วัน)
              </Typography>
              <Chip 
                label="พ.ร.บ. คอมพิวเตอร์ มาตรา ๒๖" 
                size="small" 
                color="success" 
                variant="outlined" 
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }} 
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#15803d', mt: 0.5, fontSize: '0.85rem' }}>
              จัดเก็บข้อมูลการจราจรทางคอมพิวเตอร์และบันทึกประวัติการกระทำสำคัญในระบบ (Audit Logs) ย้อนหลังอย่างน้อย 90 วันโดยอัตโนมัติ เพื่อความโปร่งใสและตรวจสอบย้อนหลังได้ตามกฎหมาย
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportExcel}
            disabled={exporting || filteredLogs.length === 0}
            sx={{ fontWeight: 'bold', borderRadius: 1.5, textTransform: 'none', px: 2 }}
          >
            {exporting ? 'กำลังส่งออก...' : 'ส่งออก Excel (.xlsx)'}
          </Button>
        </Box>
      </Paper>

      {/* Filter Panel */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              ตัวกรองและค้นหาข้อมูล (Filters & Search)
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            พบข้อมูลทั้งหมด {filteredLogs.length} รายการ (จากช่วงเวลาที่เลือก)
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {/* Timeframe selector */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="กรอบเวลาการจัดเก็บข้อมูล (Timeframe)"
              value={timeframe}
              onChange={(e) => handleTimeframeChange(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            >
              <MenuItem value="90">ย้อนหลัง 90 วัน (ตาม พ.ร.บ. คอมพิวเตอร์)</MenuItem>
              <MenuItem value="30">ย้อนหลัง 30 วัน</MenuItem>
              <MenuItem value="7">ย้อนหลัง 7 วัน</MenuItem>
              <MenuItem value="today">วันนี้ (Today)</MenuItem>
              <MenuItem value="all">ทั้งหมดในฐานข้อมูล</MenuItem>
              <MenuItem value="custom">กำหนดช่วงวันที่เอง (Custom Date)</MenuItem>
            </TextField>
          </Grid>

          {/* Custom Date Pickers */}
          {timeframe === 'custom' && (
            <>
              <Grid size={{ xs: 12, sm: 3, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="ตั้งแต่วันที่"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="ถึงวันที่"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={handleCustomDateSearch}
                  disabled={loading || (!startDate && !endDate)}
                  sx={{ height: '40px' }}
                >
                  ค้นหาช่วงวัน
                </Button>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 6, md: timeframe === 'custom' ? 3 : 4 }}>
            <TextField
              fullWidth
              size="small"
              label="ค้นหาผู้กระทำ"
              placeholder="ชื่อ-นามสกุล หรือ Username..."
              value={searchUser}
              onChange={(e) => { setSearchUser(e.target.value); setPage(0); }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" style={{ opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: timeframe === 'custom' ? 3 : 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="ประเภทผู้ใช้งาน"
              value={searchRole}
              onChange={(e) => { setSearchRole(e.target.value); setPage(0); }}
            >
              <MenuItem value="ALL">ทั้งหมด (All Roles)</MenuItem>
              <MenuItem value="ADMIN">ผู้ดูแลระบบ (Admin)</MenuItem>
              <MenuItem value="STAFF">เจ้าหน้าที่ (Staff)</MenuItem>
              <MenuItem value="SUPERVISOR">อาจารย์นิเทศก์</MenuItem>
              <MenuItem value="CO_TEACHER">ครูพี่เลี้ยง</MenuItem>
              <MenuItem value="STUDENT">นักศึกษา</MenuItem>
              <MenuItem value="DEAN">ผู้บริหาร (Dean)</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="IP Address"
              placeholder="เช่น 125.26.12.8..."
              value={searchIp}
              onChange={(e) => { setSearchIp(e.target.value); setPage(0); }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="กิจกรรม หรือ รหัส/ชื่อนักศึกษา/สาขาวิชา ที่ถูกประเมิน"
              placeholder="เช่น SUBMIT_EVALUATION, 651128..., ภาษาไทย, เข้าสู่ระบบ..."
              value={searchAction}
              onChange={(e) => { setSearchAction(e.target.value); setPage(0); }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SchoolIcon fontSize="small" style={{ opacity: 0.6 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!searchUser && searchRole === 'ALL' && !searchIp && !searchAction}
              sx={{ height: '40px', px: 2 }}
            >
              ล้างตัวกรอง
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Audit Logs Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 2 }}>
        {loading && <LinearProgress />}
        <Table sx={{ minWidth: 1000 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell sx={{ fontWeight: 'bold', width: '160px' }}>วัน-เวลา</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '220px' }}>ผู้กระทำ (Who)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '170px' }}>ประเภทผู้ใช้งาน</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '210px' }}>กิจกรรม (Action)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '170px' }}>เป้าหมาย / นศ. ที่รับการประเมิน</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '140px' }}>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log: any) => (
              <CollapsibleRow key={log.id} log={log} />
            ))}
            {!loading && filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="textSecondary">ไม่พบข้อมูลบันทึกกิจกรรมที่ตรงกับตัวกรอง</Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">กำลังโหลดบันทึกกิจกรรม...</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200]}
          component="div"
          count={filteredLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default AuditLogs;
